import React, { useState, useEffect, useCallback, useContext } from 'react';
import { fetchAllowanceSplits, updateAllowanceSplits } from '../../api';
import { UserContext } from '../../context/UserContext';
import MyButton from '../basic/MyButton';
import Notification from '../Notification/Notification';
import './IncomeSplit.css';

function IncomeSplits() {
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const { user } = useContext(UserContext);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  const calculateTotalPercentage = useCallback((currentSplits) => {
    const total = currentSplits.reduce((sum, split) => sum + (parseFloat(split.percentage) || 0), 0);
    setTotalPercentage(parseFloat(total.toFixed(1)));
  }, []);

  const fetchSplits = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAllowanceSplits(user.details.user_id);
      const formattedSplits = data.map(split => ({
        accountId: split.account_id,
        accountName: split.account_name,
        percentage: split.allowance_splits[0]?.percentage || 0
      }));
      setSplits(formattedSplits);
      calculateTotalPercentage(formattedSplits);
    } catch (err) {
      console.error("Error fetching splits:", err);
      setError('Failed to load allowance splits. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user.details.user_id, calculateTotalPercentage]);

  useEffect(() => {
    fetchSplits();
  }, [fetchSplits]);

  const handlePercentageChange = (accountId, value) => {
    if (!user.details.allow_splits_edit) return;
    const updatedSplits = splits.map(split => 
      split.accountId === accountId 
        ? { ...split, percentage: value }
        : split
    );
    setSplits(updatedSplits);
    calculateTotalPercentage(updatedSplits);
    setIsFormChanged(true);
  };

  const handleSubmit = async () => {
    if (!user.details.allow_splits_edit) return;
    if (totalPercentage !== 100) {
      setError('Total percentage must equal 100%');
      return;
    }

    setLoading(true);
    setError('');

    const formattedSplits = splits.map(split => ({
      account_id: split.accountId,
      percentage: parseFloat(split.percentage) || 0
    }));

    try {
      await updateAllowanceSplits(user.details.user_id, formattedSplits);
      setNotificationMessage('Splits updated successfully.');
      setShowNotification(true);
      setIsFormChanged(false);
    } catch (err) {
      setError('Failed to update splits. Please try again.');
      console.error("Error in update of splits: ", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const distributeEvenly = () => {
    if (!user.details.allow_splits_edit || splits.length === 0) return;
  
    const evenPercentage = Math.floor(100 / splits.length);
    let totalPercentage = evenPercentage * splits.length;
    const difference = 100 - totalPercentage;
  
    const updatedSplits = splits.map((split, index) => {
      if (index === 0) {
        return { ...split, percentage: evenPercentage + difference };
      }
      return { ...split, percentage: evenPercentage };
    });
  
    setSplits(updatedSplits);
    calculateTotalPercentage(updatedSplits);
    setIsFormChanged(true);
  };
  
  const resetSplits = () => {
    if (!user.details.allow_splits_edit) return;
    fetchSplits();
    setIsFormChanged(false);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="income-splits-container">
      {showNotification && (
        <Notification 
          message={notificationMessage} 
          type="success" 
          onClose={handleCloseNotification} 
        />
      )}
      <h2 className="income-splits-title">Edit Income Splits</h2>
      <p className="income-splits-description">
        Adjust how deposits are split between accounts. Total must equal 100%.
      </p>
      <div className="income-splits-total-indicator">
        <svg viewBox="0 0 36 36">
          <path
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke={totalPercentage === 100 ? 'green' : totalPercentage > 100 ? 'red' : 'purple'}
            strokeWidth="3"
            strokeDasharray={`${totalPercentage}, 100`}
          />
          <text x="19" y="18" textAnchor="middle" dominantBaseline="middle" fontSize="6">{totalPercentage}%</text>
        </svg>
      </div>
      <div className="income-splits-accounts-list">
        {splits.map(split => (
          <div key={split.accountId} className="income-splits-account-card">
            <h3 className="income-splits-account-name">{split.accountName}</h3>
            <input
              className="income-splits-slider"
              type="range"
              min="0"
              max="100"
              value={split.percentage}
              onChange={(e) => handlePercentageChange(split.accountId, e.target.value)}
              disabled={!user.details.allow_splits_edit}
            />
            <input
              className="income-splits-input"
              type="number"
              min="0"
              max="100"
              value={split.percentage}
              onChange={(e) => handlePercentageChange(split.accountId, e.target.value)}
              disabled={!user.details.allow_splits_edit}
            />
            <div 
              className="income-splits-circle"
              style={{ 
                background: `conic-gradient(#3498db 0deg, #3498db ${split.percentage * 3.6}deg, #eee ${split.percentage * 3.6}deg)`
              }}
            >
              {split.percentage}%
            </div>
          </div>
        ))}
      </div>
      
      {user.details.allow_splits_edit ? (
        <div className="income-splits-actions">
          <MyButton
            text="Distribute Evenly"
            onClick={distributeEvenly}
            backgroundColor="white"
            color="#333"
            hoverBackgroundColor="#e9ecef"
            hoverColor="#333"
          />
          <MyButton
            text="Reset"
            onClick={resetSplits}
            backgroundColor="white"
            color="#333"
            hoverBackgroundColor="#e9ecef"
            hoverColor="#333"
          />
          <MyButton
            text="Save Changes"
            onClick={handleSubmit}
            disabled={loading || totalPercentage !== 100 || !isFormChanged}
            color="white"
            hoverColor="white"
          />
        </div>
      ) : (
        <div className="income-splits-message">
          <p>Oops! Looks like you can't edit your income splits right now. 🔒</p>
          <p>If you want to make changes, ask your parent or guardian to unlock this super power for you! 🦸‍♂️✨</p>
        </div>
      )}
      {error && <div className="income-splits-error-message">{error}</div>}
    </div>
  );
}

export default IncomeSplits;